import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ListRowComponent } from '../../../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../../../@commons/ui-components/list-row/list-section.component';
import { ForecastMiniComponent, roundDegrees } from '../../../@commons/ui-components/forecast-mini/forecast-mini.component';
import { VwDatePipe } from '../../../@commons/pipes/vw-date.pipe';
import { VwNumberPipe } from '../../../@commons/pipes/vw-number.pipe';
import { ClockIconComponent } from '../../../@icons/clock-icon.component';
import {
  BandDay,
  BandMoon,
  BandWeather,
  MoonGroup,
  NextWeather,
  WeatherCondition,
  moonGroup,
  moonLitPath,
  nextWeather,
} from './weather-moon-band.model';

/** Jours suivants montrés en mini. */
const NEXT_DAYS = 3;

/**
 * La météo et la lune de l'accueil (`vw-weather-moon-band`) : une carte
 * blanche en deux lignes, juste sous le message du jour. Chaque ligne ouvre
 * sa page, où Premium apporte sa valeur.
 *
 * - Météo (64 px) : le pictogramme du jour, la suite du temps en un mot
 *   (« Doux demain », « Pluie mercredi »), « nuit -1°, jour 14° » (la nuit qui
 *   vient, le jour d'aujourd'hui), puis les
 *   3 jours suivants en mini. Elle ne répète jamais le message du jour :
 *   `suppressSignals` dit ce qu'il annonce déjà, la ligne dit ce qui vient après.
 * - Météo ancienne (`stale` : pas de prévision du jour local qui couvre
 *   aujourd'hui, ou hors ligne) ou absente : « Pas à jour » en ambre et
 *   l'heure des données, sans les 3 jours ni rien de rassurant fondé sur la
 *   météo.
 * - Lune (52 px) : le dessin de la phase, le type de jour, la phase en forme
 *   courte et le sens. Elle ne crée jamais de geste.
 *
 * Composant d'affichage : tout arrive par les entrées (voir
 * `weather-moon-band.model.ts` pour les nourrir depuis ce que l'accueil charge).
 * Usage : `<vw-weather-moon-band [weather]="…" [moon]="…" [stale]="…"
 *            [suppressSignals]="['FROST']" (openWeather)="…" (openMoon)="…" />`
 */
@Component({
  selector: 'vw-weather-moon-band',
  standalone: true,
  imports: [TranslatePipe, ListRowComponent, ListSectionComponent, ForecastMiniComponent, VwDatePipe, VwNumberPipe, ClockIconComponent],
  templateUrl: './weather-moon-band.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WeatherMoonBandComponent implements OnChanges {
  /** Aujourd'hui et les jours suivants ; absente, la ligne dit « Pas à jour », sans heure. */
  @Input() weather: BandWeather | null = null;
  /** Absente, la ligne de la lune ne s'affiche pas. */
  @Input() moon: BandMoon | null = null;
  /** Heure des données quand la météo n'est plus à jour ; `null` quand elle l'est. */
  @Input() stale: Date | null = null;
  /** Ce que le message du jour annonce déjà (le gel) : la ligne météo ne le répète pas. */
  @Input() suppressSignals: readonly WeatherCondition[] = [];

  @Output() openWeather = new EventEmitter<void>();
  @Output() openMoon = new EventEmitter<void>();

  next: NextWeather | null = null;
  nextDays: BandDay[] = [];
  moonGroup: MoonGroup | null = null;
  moonLit = '';
  readonly round = roundDegrees;

  ngOnChanges(): void {
    this.next = this.weather ? nextWeather(this.weather, this.suppressSignals) : null;
    this.nextDays = this.weather?.next.slice(0, NEXT_DAYS) ?? [];
    this.moonGroup = moonGroup(this.moon?.phase);
    this.moonLit = moonLitPath(this.moon?.phase);
  }

  /**
   * La nuit qui vient, comme le message du jour la lit : la plus basse de demain
   * matin (« cette nuit »). Celle d'aujourd'hui est passée ; elle ne sert que
   * sans lendemain dans la prévision. Le message et la bande disent le même chiffre.
   */
  get night(): number | null {
    return this.weather ? (this.weather.next[0]?.min ?? this.weather.today.min) : null;
  }

  /** Le gel et la pluie se disent en bleu, comme le message du jour ; le reste dans l'encre. */
  get nextTone(): 'cold' | 'default' {
    return this.next?.condition === 'FROST' || this.next?.condition === 'RAIN' ? 'cold' : 'default';
  }

  /** Ancienne ou absente : la météo ne dit plus rien du temps qui vient. */
  get isStale(): boolean {
    return !!this.stale || !this.weather;
  }

  /** Les données datent d'aujourd'hui : l'heure suffit. */
  get staleToday(): boolean {
    return !!this.stale && this.stale.toDateString() === new Date().toDateString();
  }
}
